import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { SOLUCOES_LABELS } from '@/pages/painel/lib/radarConstants';
import { FAIXA_LABELS, ORIGEM_LABELS, formatBRL, formatPercent } from '@/pages/painel/lib/comercial';
import InteligenciaComercial from './InteligenciaComercial';
import InteligenciaConteudo from './InteligenciaConteudo';

interface Realizado {
  faturamento: number;
  lucro: number;
  margem_media: number | null;
  negocios: number;
  ticket_medio: number | null;
  maior_venda: number | null;
  tempo_medio_fechamento: number | null;
}

interface Pipeline {
  aberto: number;
  ponderado: number;
  lucro_potencial: number;
  lucro_ponderado: number;
  previsao_30: number;
  previsao_60: number;
  previsao_90: number;
}

interface Grupo { key: string; faturamento: number; negocio: number; lucro: number }

interface ResultadosData {
  realizado: Realizado;
  pipeline: Pipeline;
  por_origem: Grupo[];
  por_faixa: Grupo[];
  por_solucao: Grupo[];
  por_empresa: Grupo[];
  por_cidade: Grupo[];
  por_uf: Grupo[];
}

function val(v: number | null | undefined): string {
  return v == null || v === 0 ? 'Não calculável' : formatBRL(v);
}

function valPct(v: number | null | undefined): string {
  return v == null ? 'Não calculável' : formatPercent(v);
}

export default function Resultados() {
  const { password } = useOutletContext<{ password: string }>();
  const [data, setData] = useState<ResultadosData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'resultados' | 'inteligencia' | 'conteudo'>('resultados');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'resultados', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as ResultadosData & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setData(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar resultados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const renderGrupo = (grupos: Grupo[], labelMap?: Record<string, string>) => {
    if (!grupos || grupos.length === 0) return <p className="text-xs text-gray-400">Sem dados de negócios ganhos ainda.</p>;
    return (
      <div className="space-y-1.5">
        {grupos.map((g) => (
          <div key={g.key} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-gray-700 truncate">{labelMap?.[g.key] ?? (g.key === 'nao_informado' ? 'Não informado' : g.key)}</span>
            <span className="text-gray-500 whitespace-nowrap">{formatBRL(g.faturamento)} <span className="text-gray-400 text-xs">· {g.negocio} neg.</span></span>
          </div>
        ))}
      </div>
    );
  };

  const KPI = ({ label, value, icon, tone = 'text-gray-900' }: { label: string; value: string; icon: string; tone?: string }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1.5"><i className={icon}></i>{label}</p>
      <p className={`text-base font-bold truncate ${tone}`}>{value}</p>
    </div>
  );

  const TAB_BAR = (
    <div className="bg-white rounded-2xl border border-gray-200 p-2 flex items-center gap-1 flex-wrap">
      {[
        { key: 'resultados', label: 'Resultados', icon: 'ri-line-chart-line' },
        { key: 'inteligencia', label: 'Inteligência Comercial', icon: 'ri-brain-line' },
        { key: 'conteudo', label: 'Conteúdo', icon: 'ri-question-answer-line' },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key as 'resultados' | 'inteligencia' | 'conteudo')}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
            tab === t.key ? 'bg-[#E8610A] text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <i className={t.icon}></i>
          {t.label}
        </button>
      ))}
    </div>
  );

  if (tab === 'inteligencia') {
    return <div className="space-y-6">{TAB_BAR}<InteligenciaComercial /></div>;
  }
  if (tab === 'conteudo') {
    return <div className="space-y-6">{TAB_BAR}<InteligenciaConteudo /></div>;
  }

  return (
    <div className="space-y-6">
      {TAB_BAR}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-green-50 text-green-700 rounded-xl">
            <i className="ri-line-chart-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Resultados</h2>
            <p className="text-sm text-gray-500">Realizado vs. previsão, separados. Leitura, sem inventar números.</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60">
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i> Atualizar
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && !data ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <>
          {/* REALIZADO */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-green-50 text-green-700"><i className="ri-check-double-line"></i></div>
              <h3 className="font-bold text-gray-900">Resultados realizados</h3>
              <span className="text-[11px] text-gray-400">(apenas negócios ganhos)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <KPI label="Faturamento" value={val(data.realizado.faturamento)} icon="ri-money-dollar-circle-line" tone="text-gray-900" />
              <KPI label="Lucro" value={val(data.realizado.lucro)} icon="ri-funds-line" tone="text-green-700" />
              <KPI label="Margem média" value={valPct(data.realizado.margem_media)} icon="ri-percent-line" />
              <KPI label="Número de negócios" value={String(data.realizado.negocios)} icon="ri-file-list-3-line" />
              <KPI label="Ticket médio" value={val(data.realizado.ticket_medio)} icon="ri-price-tag-3-line" />
              <KPI label="Maior venda" value={val(data.realizado.maior_venda)} icon="ri-trophy-line" />
              <KPI label="Tempo médio de fechamento" value={data.realizado.tempo_medio_fechamento != null ? `${data.realizado.tempo_medio_fechamento} dias` : 'Não calculável'} icon="ri-time-line" />
            </div>
          </section>

          {/* PIPELINE / PREVISÃO */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600"><i className="ri-hourglass-line"></i></div>
              <h3 className="font-bold text-gray-900">Pipeline / Previsão</h3>
              <span className="text-[11px] text-gray-400">(oportunidades abertas)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <KPI label="Pipeline aberto" value={val(data.pipeline.aberto)} icon="ri-funds-line" />
              <KPI label="Pipeline ponderado" value={val(data.pipeline.ponderado)} icon="ri-scales-3-line" />
              <KPI label="Lucro potencial" value={val(data.pipeline.lucro_potencial)} icon="ri-money-dollar-circle-line" tone="text-green-700" />
              <KPI label="Lucro ponderado" value={val(data.pipeline.lucro_ponderado)} icon="ri-coins-line" tone="text-green-700" />
              <KPI label="Previsão 30 dias" value={val(data.pipeline.previsao_30)} icon="ri-calendar-line" />
              <KPI label="Previsão 60 dias" value={val(data.pipeline.previsao_60)} icon="ri-calendar-2-line" />
              <KPI label="Previsão 90 dias" value={val(data.pipeline.previsao_90)} icon="ri-calendar-check-line" />
            </div>
          </section>

          {/* BREAKDOWNS */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por origem</h4>
              {renderGrupo(data.por_origem, ORIGEM_LABELS)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por faixa comercial</h4>
              {renderGrupo(data.por_faixa, FAIXA_LABELS)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por solução</h4>
              {renderGrupo(data.por_solucao, SOLUCOES_LABELS)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por empresa</h4>
              {renderGrupo(data.por_empresa)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por cidade</h4>
              {renderGrupo(data.por_cidade)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por UF</h4>
              {renderGrupo(data.por_uf)}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}